import React from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '../avatar'
import { SafeMarkdown } from 'components/Common/SafeMarkdown/SafeMarkdown.component'
import { Message, MessageProps, UserInfo } from './types'

const SpeakerIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.9283 4.69684C18.8573 7.62577 18.8573 12.3745 15.9283 15.3034M13.7189 6.90662C15.4274 8.61516 15.4274 11.3853 13.7189 13.0938M5.625 6.87518L9.55806 2.94212C9.95179 2.5484 10.625 2.82725 10.625 3.38407V16.6163C10.625 17.1731 9.95179 17.452 9.55806 17.0582L5.625 13.1252H3.75755C3.02476 13.1252 2.33825 12.7031 2.14364 11.9967C1.96854 11.361 1.875 10.6915 1.875 10.0002C1.875 9.30886 1.96854 8.63938 2.14364 8.00371C2.33825 7.29724 3.02476 6.87518 3.75755 6.87518H5.625Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const StopIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="12" height="12" rx="2" fill="currentColor" />
  </svg>
)

const CopyIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.125 14.375V17.1875C13.125 17.7053 12.7053 18.125 12.1875 18.125H4.0625C3.54473 18.125 3.125 17.7053 3.125 17.1875V6.5625C3.125 6.04473 3.54473 5.625 4.0625 5.625H5.625C6.05089 5.625 6.46849 5.6605 6.875 5.7287M13.125 14.375H15.9375C16.4553 14.375 16.875 13.9553 16.875 13.4375V9.375C16.875 5.65876 14.1721 2.5738 10.625 1.9787C10.2185 1.9105 9.80089 1.875 9.375 1.875H7.8125C7.29473 1.875 6.875 2.29473 6.875 2.8125V5.7287M13.125 14.375H7.8125C7.29473 14.375 6.875 13.9553 6.875 13.4375V5.7287M16.875 11.25V9.6875C16.875 8.1342 15.6158 6.875 14.0625 6.875H12.8125C12.2947 6.875 11.875 6.45527 11.875 5.9375V4.6875C11.875 3.1342 10.6158 1.875 9.0625 1.875H8.125"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 10.5L8 14.5L16 5.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.charAt(0)?.toUpperCase() || ''
  const last = lastName?.charAt(0)?.toUpperCase() || ''
  return first + last || 'U'
}

const getUserDisplayName = (userInfo?: UserInfo): string => {
  if (userInfo?.firstName || userInfo?.lastName) {
    return `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim()
  }
  return 'You'
}

const getFileIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return '🖼️'
  if (mimeType.includes('pdf')) return '📄'
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝'
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊'
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📋'
  if (mimeType.includes('video/')) return '🎥'
  if (mimeType.includes('audio/')) return '🎵'
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('archive'))
    return '🗜️'
  if (mimeType.includes('text/')) return '📄'
  return '📎'
}

const formatTime = (date: Date) => {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

const formatDateSeparator = (date: Date): string => {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()

  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()

  if (isToday) return 'Today'
  if (isYesterday) return 'Yesterday'
  return date.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })
}

const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  )
}

export function CoachMessage({
  message,
  showHeader,
  isLastMessage,
  isSpeaking,
  copiedMessageId,
  onSpeak,
  onCopy,
}: MessageProps) {
  return (
    <div className="flex items-start gap-3">
      {showHeader ? (
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src="/images/CL-avatar.png" alt="Coach Logic" />
          <AvatarFallback className="bg-app-primary-60 text-app-white-100">CL</AvatarFallback>
        </Avatar>
      ) : (
        <div className="w-10 shrink-0" />
      )}
      <div className="flex flex-col gap-1 max-w-[70%]">
        {showHeader && (
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-sm">Coach Logic</span>
            <span className="text-xs text-app-grey-50">{formatTime(message.timestamp)}</span>
          </div>
        )}
        <div className="bg-app-grey-20 rounded-lg px-3 py-2 text-sm leading-relaxed w-fit">
          <SafeMarkdown className="text-sm text-app-black-100">{message.content}</SafeMarkdown>
        </div>
        {isLastMessage && (
          <div className="flex items-center gap-1 pt-2">
            <button
              onClick={() => onSpeak?.(message.content)}
              title={isSpeaking ? 'Stop reading' : 'Read message aloud'}
              className={`p-1.5 cursor-pointer border-none rounded-md transition-all duration-150 ${
                isSpeaking
                  ? 'text-app-primary-60 bg-app-primary-10 hover:bg-app-primary-20'
                  : 'text-app-grey-60 bg-transparent hover:text-app-grey-80 hover:bg-app-grey-20 active:scale-95'
              }`}
            >
              {isSpeaking ? <StopIcon className="w-5 h-5" /> : <SpeakerIcon className="w-5 h-5" />}
            </button>
            <button
              onClick={() => onCopy?.(message.content, message.id)}
              title={copiedMessageId === message.id ? 'Copied!' : 'Copy message to clipboard'}
              className={`p-1.5 cursor-pointer border-none rounded-md transition-all duration-150 ${
                copiedMessageId === message.id
                  ? 'text-app-green-60 bg-app-green-10'
                  : 'text-app-grey-60 bg-transparent hover:text-app-grey-80 hover:bg-app-grey-20 active:scale-95'
              }`}
            >
              {copiedMessageId === message.id ? (
                <CheckIcon className="w-5 h-5" />
              ) : (
                <CopyIcon className="w-5 h-5" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export function UserMessage({ message, showHeader, userInfo }: MessageProps) {
  const displayName = getUserDisplayName(userInfo)
  const initials = getInitials(userInfo?.firstName, userInfo?.lastName)

  return (
    <div className="flex items-start gap-3 justify-end">
      <div className="flex flex-col gap-1 items-end max-w-[70%]">
        {showHeader && (
          <div className="flex items-baseline gap-2 justify-end">
            <span className="text-xs text-app-grey-50">{formatTime(message.timestamp)}</span>
            <span className="font-semibold text-sm">{displayName}</span>
          </div>
        )}
        <div className="bg-app-primary-60 text-app-white-100 rounded-lg px-3 py-2 text-sm leading-relaxed w-fit max-w-full">
          {message.isVoiceMessage ? (
            <div>
              <div className="flex items-center gap-2 mb-2 p-2 bg-app-white-10 rounded">
                <span className="text-base">🎤</span>
                <div className="flex-1">
                  <div className="text-xs font-medium">
                    Voice Message
                    {message.voiceDuration && (
                      <span className="opacity-80 ml-2">
                        ({Math.round(message.voiceDuration)}s)
                      </span>
                    )}
                  </div>
                </div>
                {message.attachments && message.attachments[0] && (
                  <button
                    className="bg-app-white-20 border-none rounded-full w-8 h-8 flex items-center justify-center cursor-pointer"
                    onClick={() => {
                      const audio = new Audio(message.attachments![0].url)
                      audio.play().catch((e) => console.error('Audio play failed:', e))
                    }}
                    title="Play voice message"
                  >
                    ▶️
                  </button>
                )}
              </div>
              {message.voiceTranscript && (
                <div className="text-sm italic opacity-90 text-app-white-100">
                  "{message.voiceTranscript}"
                </div>
              )}
            </div>
          ) : (
            <SafeMarkdown
              className="text-sm"
              components={{
                p: ({ children }: { children: React.ReactNode }) => (
                  <p className="mb-2 last:mb-0 text-app-white-100">{children}</p>
                ),
                ul: ({ children }: { children: React.ReactNode }) => (
                  <ul className="list-disc ml-4 mb-2 last:mb-0 text-app-white-100">{children}</ul>
                ),
                ol: ({ children }: { children: React.ReactNode }) => (
                  <ol className="list-decimal ml-4 mb-2 last:mb-0 text-app-white-100">
                    {children}
                  </ol>
                ),
                li: ({ children }: { children: React.ReactNode }) => (
                  <li className="mb-1 last:mb-0 text-app-white-100">{children}</li>
                ),
                strong: ({ children }: { children: React.ReactNode }) => (
                  <strong className="font-semibold text-app-white-100">{children}</strong>
                ),
                em: ({ children }: { children: React.ReactNode }) => (
                  <em className="italic text-app-white-100">{children}</em>
                ),
                code: ({ children }: { children: React.ReactNode }) => (
                  <code className="bg-app-white-20 px-1 py-0.5 rounded text-sm font-mono text-app-white-100">
                    {children}
                  </code>
                ),
              }}
            >
              {message.content}
            </SafeMarkdown>
          )}
          {message.attachments && message.attachments.length > 0 && !message.isVoiceMessage && (
            <div className="mt-2">
              {message.attachments.map((attachment, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-2 p-2 bg-app-white-10 rounded cursor-pointer ${
                    idx < message.attachments!.length - 1 ? 'mb-1' : ''
                  }`}
                  onClick={() => {
                    const link = document.createElement('a')
                    link.href = attachment.url
                    link.download = attachment.fileName
                    link.target = '_blank'
                    link.rel = 'noopener noreferrer'
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                  }}
                >
                  {attachment.type === 'image' ? (
                    <img
                      src={attachment.url}
                      alt={attachment.fileName}
                      className="max-w-[200px] max-h-[150px] rounded pointer-events-none"
                    />
                  ) : (
                    <>
                      <span className="text-lg">{getFileIcon(attachment.mimeType)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs break-all">{attachment.fileName}</div>
                        <div className="text-[0.7rem] opacity-80">
                          {Math.round(attachment.fileSize / 1024)} KB
                        </div>
                      </div>
                      <span className="text-xs opacity-60">↓</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showHeader ? (
        <Avatar className="h-10 w-10 shrink-0">
          {userInfo?.avatar ? <AvatarImage src={userInfo.avatar} alt={displayName} /> : null}
          <AvatarFallback
            className="font-semibold text-sm"
            style={{
              background: 'linear-gradient(180deg, #E6F6D1 0%, #CFEEA8 100%)',
              color: '#16a34a',
            }}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="w-10 shrink-0" />
      )}
    </div>
  )
}

export function TypingIndicator() {
  return (
    <div className="flex items-start gap-3">
      <Avatar className="h-10 w-10 shrink-0">
        <AvatarImage src="/images/CL-avatar.png" alt="Coach Logic" />
        <AvatarFallback className="bg-app-primary-60 text-app-white-100">CL</AvatarFallback>
      </Avatar>
      <div className="bg-app-grey-20 rounded-lg px-4 py-3 flex items-center gap-1">
        <span className="w-2 h-2 bg-app-grey-50 rounded-full animate-pulse" />
        <span className="w-2 h-2 bg-app-grey-50 rounded-full animate-pulse [animation-delay:0.2s]" />
        <span className="w-2 h-2 bg-app-grey-50 rounded-full animate-pulse [animation-delay:0.4s]" />
      </div>
    </div>
  )
}

function DateSeparator({ date }: { date: Date }) {
  return (
    <div className="flex items-center justify-center py-3 mb-3">
      <span className="text-xs text-app-grey-50 px-3 py-1">{formatDateSeparator(date)}</span>
    </div>
  )
}

export function MessageList({
  messages,
  isTyping,
  isSpeaking,
  copiedMessageId,
  onSpeak,
  onCopy,
  userInfo,
}: {
  messages: Message[]
  isTyping: boolean
  isSpeaking: boolean
  copiedMessageId: string | null
  onSpeak: (text: string) => void
  onCopy: (text: string, messageId: string) => void
  userInfo?: UserInfo
}) {
  return (
    <div className="flex flex-col">
      {messages.map((message, index) => {
        const previousMessage = index > 0 ? messages[index - 1] : null
        const nextMessage = index < messages.length - 1 ? messages[index + 1] : null
        const showHeader = !previousMessage || previousMessage.sender !== message.sender
        const showDateSeparator =
          !previousMessage || !isSameDay(previousMessage.timestamp, message.timestamp)

        // Show icons on coach messages that are:
        // 1. The last message of a coach "turn" (next message is from user or no next message)
        // 2. NOT the greeting message (first message, typically "Hi [name]!")
        const isLastOfCoachTurn =
          message.sender === 'coach' && (!nextMessage || nextMessage.sender === 'user')
        const isGreetingMessage = index === 0 && message.id === 'greeting'
        const showIcons = isLastOfCoachTurn && !isGreetingMessage

        return (
          <div key={message.id} className={showHeader ? 'mt-8 first:mt-0' : 'mt-2'}>
            {showDateSeparator && <DateSeparator date={message.timestamp} />}
            {message.sender === 'coach' ? (
              <CoachMessage
                message={message}
                showHeader={showHeader}
                isLastMessage={showIcons}
                isSpeaking={isSpeaking}
                copiedMessageId={copiedMessageId}
                onSpeak={onSpeak}
                onCopy={onCopy}
              />
            ) : (
              <UserMessage message={message} showHeader={showHeader} userInfo={userInfo} />
            )}
          </div>
        )
      })}
      {isTyping && (
        <div className="mt-8">
          <TypingIndicator />
        </div>
      )}
    </div>
  )
}
